import { writeFileSync, existsSync, readFileSync } from 'fs';
import { join } from 'path';

export interface EnvVariable {
  key: string;
  description: string;
  defaultValue?: string;
  required?: boolean;
}

export class EnvGenerator {
  private variables: EnvVariable[] = [
    {
      key: 'VIBECODE_AI_PROVIDER',
      description: 'AI provider for chat functionality (openai, anthropic)',
      defaultValue: 'openai',
    },
    {
      key: 'VIBECODE_AI_API_KEY',
      description: 'API key for your AI provider',
      required: true,
    },
    {
      key: 'VIBECODE_AI_MODEL',
      description: 'AI model to use (e.g., gpt-4, claude-3)',
      defaultValue: 'gpt-4',
    },
    {
      key: 'VIBECODE_AUTH_SECRET',
      description: 'Secret key for authentication (generate with: openssl rand -base64 32)',
      required: false,
    },
    {
      key: 'VIBECODE_CACHE_DIR',
      description: 'Directory for caching (default: .vibecode/cache)',
      defaultValue: '.vibecode/cache',
    },
    {
      key: 'VIBECODE_DEBUG',
      description: 'Enable debug logging',
      defaultValue: 'false',
    },
  ];

  constructor(private features: { ai?: boolean; authentication?: boolean }) {
    // Filter variables based on enabled features
    if (!features.ai) {
      this.variables = this.variables.filter(v => !v.key.includes('_AI_'));
    }
    if (!features.authentication) {
      this.variables = this.variables.filter(v => !v.key.includes('_AUTH_'));
    }
  }

  generate(projectRoot: string): string {
    const envPath = join(projectRoot, '.env.example');
    const envLocalPath = join(projectRoot, '.env.local');
    
    // Generate content
    const content = this.generateContent();
    
    // Write .env.example
    writeFileSync(envPath, content);
    
    // Create .env.local if it doesn't exist
    if (!existsSync(envLocalPath)) {
      writeFileSync(envLocalPath, content);
    }
    
    return envPath;
  }

  private generateContent(): string {
    const lines: string[] = [
      '# Vibecode DevTools Configuration',
      '# Copy this file to .env.local and fill in your values',
      '',
    ];

    // Group variables by category
    const aiVars = this.variables.filter(v => v.key.includes('_AI_'));
    const authVars = this.variables.filter(v => v.key.includes('_AUTH_'));
    const otherVars = this.variables.filter(v => 
      !v.key.includes('_AI_') && !v.key.includes('_AUTH_')
    );

    // Add AI variables
    if (aiVars.length > 0) {
      lines.push('# AI Configuration');
      aiVars.forEach(v => this.addVariable(lines, v));
      lines.push('');
    }

    // Add Auth variables
    if (authVars.length > 0) {
      lines.push('# Authentication');
      authVars.forEach(v => this.addVariable(lines, v));
      lines.push('');
    }

    // Add other variables
    if (otherVars.length > 0) {
      lines.push('# General Configuration');
      otherVars.forEach(v => this.addVariable(lines, v));
    }

    return lines.join('\n');
  }

  private addVariable(lines: string[], variable: EnvVariable): void {
    lines.push(`# ${variable.description}`);
    if (variable.required) {
      lines.push('# Required');
    }
    const value = variable.defaultValue || '';
    lines.push(`${variable.key}=${value}`);
    lines.push('');
  }

  // Check if all required variables are set
  checkRequiredVariables(projectRoot: string): { valid: boolean; missing: string[] } {
    const envLocalPath = join(projectRoot, '.env.local');
    
    if (!existsSync(envLocalPath)) {
      return {
        valid: false,
        missing: this.variables.filter(v => v.required).map(v => v.key),
      };
    }

    const content = readFileSync(envLocalPath, 'utf-8');
    const missing: string[] = [];

    this.variables
      .filter(v => v.required)
      .forEach(variable => {
        const regex = new RegExp(`^${variable.key}=.+$`, 'm');
        if (!regex.test(content)) {
          missing.push(variable.key);
        }
      });

    return {
      valid: missing.length === 0,
      missing,
    };
  }
}